import API from '@/api/api-main';
import { ref } from 'vue';

/**
 * Cảnh báo phòng chống rửa tiền theo giao dịch trong NGÀY của một khách (ticket 513).
 *
 * Tổng mua + bán + đổi trả + đặt hàng của khách trong ngày (gộp mọi cửa hàng, bỏ phiếu đã huỷ)
 * từ 5 triệu thì không được dùng tiền mặt. Backend chặn lưu phiếu (AppDbContext.AmlCashLimit).
 */
export const AML_TOTAL_LIMIT = 5_000_000;

// objType: 13 bán hàng, 26 đặt hàng, 18 mua hàng (khách bán lại cho cửa hàng), 19 đổi trả.
const SELL_TYPES = [13, 26];

const cashOf = (doc) => Number(doc?.cashAmount) || 0;
const nonCashOf = (doc) => Number(doc?.nonCashAmount) || 0;

export const fetchDailyTransactions = (cardCode) => {
    if (!cardCode) return Promise.resolve([]);
    return API.get(`Customer/${cardCode}/transactions-today`)
        .then(({ data }) => (Array.isArray(data) ? data : data?.items || []))
        .catch(() => []);
};

export const summarizeDaily = (docs) => {
    // "Có phiếu thanh toán": phiếu chưa thu/chi tiền không tính vào ngưỡng.
    // Số tiền lấy theo dòng thanh toán chứ không theo `total`: đơn đặt hàng mới cọc thì `total`
    // là giá trị cả đơn còn tiền đã chuyển chỉ là khoản cọc.
    const list = (docs || []).filter((doc) => cashOf(doc) + nonCashOf(doc) > 0);

    const group = (match) => {
        const rows = list.filter(match);
        const cash = rows.reduce((t, doc) => t + cashOf(doc), 0);
        const nonCash = rows.reduce((t, doc) => t + nonCashOf(doc), 0);
        return { count: rows.length, cash, nonCash, total: cash + nonCash };
    };

    const buy = group((doc) => doc?.objType === 18);
    const sell = group((doc) => SELL_TYPES.includes(doc?.objType));
    const exchange = group((doc) => doc?.objType === 19);

    return {
        count: list.length,
        buy,
        sell,
        exchange,
        cash: buy.cash + sell.cash + exchange.cash,
        nonCash: buy.nonCash + sell.nonCash + exchange.nonCash,
        total: buy.total + sell.total + exchange.total
    };
};

/**
 * Nội dung popup đang cần hiện — `null` là đóng. AmlDailyWarning.vue (mount một lần ở SalePage)
 * theo dõi ref này, SearchCustomer bắn vào ngay khi tít CCCD / chọn ra khách hàng.
 */
export const amlWarning = ref(null);

// Nghiệp vụ nằm trong phạm vi cảnh báo phòng chống rửa tiền — không gồm bảo hành / kiểm định.
const AML_TYPES = ['HOA_DON', 'MUA_HANG', 'DOI_HANG', 'DAT_HANG', 'HOA_DON_VTS', 'MUA_HANG_VTS', 'DOI_TRA_HANG_VTS', 'DAT_HANG_VTS'];

// Mỗi tab mở một instance SearchCustomer, watch của cả N instance cùng bắn một lượt khi
// chọn khách — chốt này để chỉ gọi API một lần, lượt sau (khách khác / tít lại) vẫn chạy.
let inflight = null;

/** `tab` là `fnT.data[fnT.active]` — lấy khách và loại nghiệp vụ từ tab đang mở. */
export const showAmlWarning = async (tab) => {
    const { cardCode, cardName } = tab?.payload || {};
    if (!cardCode || !AML_TYPES.includes(tab?.type)) return;
    if (inflight === cardCode) return;
    inflight = cardCode;
    try {
        const sum = summarizeDaily(await fetchDailyTransactions(cardCode));
        // Chỉ cảnh báo từ giao dịch thứ 2 trong ngày trở đi: phải đã có phiếu trước đó.
        if (!sum.count) return;
        amlWarning.value = { cardName, sum };
    } finally {
        inflight = null;
    }
};
